import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    container: {
        padding: 8,
        marginTop: 15,
    },
    sectionTitle: {
        fontSize: 13,
        fontWeight: 'bold',
        marginBottom: 12,
        color: '#1e40af',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
        paddingBottom: 4,
    },
    cardContainer: {
        borderWidth: 1,
        borderColor: '#e5e7eb',
        borderRadius: 4,
        padding: 8,
        marginBottom: 12,
        backgroundColor: '#ffffff',
    },
    cardTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        marginBottom: 8,
        color: '#374151',
    },
    table: {
        display: 'flex',
        width: '100%',
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#e5e7eb',
        minHeight: 25,
        alignItems: 'center',
    },
    tableHeaderRow: {
        backgroundColor: '#f0f9ff',
        fontWeight: 'bold',
        minHeight: 28,
    },
    tableSubHeaderRow: {
        backgroundColor: '#f8fafc',
        minHeight: 25,
    },
    tableCell: {
        padding: 2,
        fontSize: 7,
        borderRightWidth: 1,
        borderRightColor: '#e5e7eb',
        textAlign: 'center',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 25,
    },
    transportModeCell: {
        width: '15%',
        textAlign: 'left',
        paddingLeft: 4,
        paddingRight: 2,
    },
    distanceCell: {
        width: '13%',
        textAlign: 'left',
        paddingLeft: 4,
        paddingRight: 2,
    },
    metricCell: {
        width: '7.2%',
        fontSize: 6,
    },
    riskCell: {
        width: '7.2%',
        padding: 1,
        fontSize: 6,
    },
    headerSpanCell: {
        width: '36%',
        backgroundColor: '#dbeafe',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: 8,
    },
    headerSpanCellGreen: {
        width: '36%',
        backgroundColor: '#dcfce7',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: 8,
    },
    subHeaderCell: {
        fontSize: 6,
        fontWeight: 'bold',
        backgroundColor: '#f1f5f9',
        padding: 1,
    },
    subHeaderCellBlue: {
        backgroundColor: '#dbeafe',
    },
    subHeaderCellGreen: {
        backgroundColor: '#dcfce7',
    },
    transportModeHeaderCell: {
        backgroundColor: '#f0f9ff',
    },
    distanceHeaderCell: {
        backgroundColor: '#f0f9ff',
    },
    riskBadge: {
        padding: 1,
        borderRadius: 2,
        fontSize: 5,
        fontWeight: 'bold',
        textAlign: 'center',
        color: '#ffffff',
        minWidth: 20,
        maxWidth: 35,
    },
    highRisk: {
        backgroundColor: '#dc2626',
    },
    mediumRisk: {
        backgroundColor: '#f59e0b',
    },
    lowRisk: {
        backgroundColor: '#059669',
    },
    noDataRow: {
        backgroundColor: '#f9fafb',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 35,
    },
    noDataText: {
        fontSize: 9,
        color: '#6b7280',
        textAlign: 'center',
    },
    evenRow: {
        backgroundColor: '#ffffff',
    },
    oddRow: {
        backgroundColor: '#f9fafb',
    },
    cellText: {
        fontSize: 6,
        lineHeight: 1.2,
    },
    headerText: {
        fontSize: 7,
        fontWeight: 'bold',
        lineHeight: 1.1,
    },
    subHeaderText: {
        fontSize: 5,
        fontWeight: 'bold',
        lineHeight: 1.1,
    },
    noteContainer: {
        marginTop: 10,
        padding: 8,
        backgroundColor: '#fffbeb',
        borderLeftWidth: 3,
        borderLeftColor: '#f59e0b',
        borderRadius: 2,
    },
    noteText: {
        fontSize: 7,
        color: '#92400e',
        lineHeight: 1.3,
        textAlign: 'justify',
    },
});

interface EvaluationResult {
    TransitMode: string;
    Distance: string;
    Distance_Unit?: string;
    LinearRegression: {
        Lean: number;
        Walk: number;
        Compression: number;
        SafetyFactor: number;
        OverallRisk: string;
    };
    TreeModel: {
        Lean: number;
        Walk: number;
        Compression: number;
        SafetyFactor: number;
        OverallRisk: string;
    };
}

interface EvaluationResultsPDFProps {
    evaluateResult: EvaluationResult[];
}

const getRiskBadgeStyle = (risk: string) => {
    const baseStyle = styles.riskBadge;
    switch (risk?.toLowerCase()) {
        case 'high':
            return [baseStyle, styles.highRisk];
        case 'medium':
            return [baseStyle, styles.mediumRisk];
        case 'low':
            return [baseStyle, styles.lowRisk];
        default:
            return [baseStyle, styles.lowRisk];
    }
};

const formatNumber = (value: number | string): string => {
    if (typeof value === 'number') {
        return value.toFixed(2);
    }
    return value?.toString() || '-';
};

export const EvaluationResultsPDF: React.FC<EvaluationResultsPDFProps> = ({
    evaluateResult
}) => {
    console.log(evaluateResult, 'evaluateResultevaluateResult')
    return (
        <View style={styles.container}>
            <Text style={styles.sectionTitle}>Results and Analysis</Text>

            <View style={styles.cardContainer}>
                <Text style={styles.cardTitle}>Risk Assessment</Text>

                <View style={styles.table}>
                    {/* Main Header Row */}
                    <View style={[styles.tableRow, styles.tableHeaderRow]}>
                        <View style={[styles.tableCell, styles.transportModeCell]}>
                            <Text style={styles.headerText}>Transport Mode</Text>
                        </View>
                        <View style={[styles.tableCell, styles.distanceCell, styles.distanceHeaderCell]}>
                            <Text style={styles.headerText}>Distance</Text>
                        </View>
                        <View style={[styles.tableCell, styles.headerSpanCell]}>
                            <Text style={styles.headerText}>Tree Based Models</Text>
                        </View>
                        <View style={[styles.tableCell, styles.headerSpanCellGreen]}>
                            <Text style={styles.headerText}>Linear Regression</Text>
                        </View>
                    </View>

                    {/* Sub Header Row */}
                    <View style={[styles.tableRow, styles.tableSubHeaderRow]}>
                        <View style={[styles.tableCell, styles.transportModeCell, styles.transportModeHeaderCell]}>
                            <Text></Text>
                        </View>
                        <View style={[styles.tableCell, styles.distanceCell, styles.distanceHeaderCell]}>
                            <Text></Text>
                        </View>
                        {/* Tree Based Models columns */}
                        <View style={[styles.tableCell, styles.metricCell, styles.subHeaderCell, styles.subHeaderCellBlue]}>
                            <Text style={styles.subHeaderText}>Lean</Text>
                        </View>
                        <View style={[styles.tableCell, styles.metricCell, styles.subHeaderCell, styles.subHeaderCellBlue]}>
                            <Text style={styles.subHeaderText}>Walk</Text>
                        </View>
                        <View style={[styles.tableCell, styles.metricCell, styles.subHeaderCell, styles.subHeaderCellBlue]}>
                            <Text style={styles.subHeaderText}>Comp</Text>
                        </View>
                        <View style={[styles.tableCell, styles.metricCell, styles.subHeaderCell, styles.subHeaderCellBlue]}>
                            <Text style={styles.subHeaderText}>Safety</Text>
                        </View>
                        <View style={[styles.tableCell, styles.riskCell, styles.subHeaderCell, styles.subHeaderCellBlue]}>
                            <Text style={styles.subHeaderText}>Risk</Text>
                        </View>
                        {/* Linear Regression columns */}
                        <View style={[styles.tableCell, styles.metricCell, styles.subHeaderCell, styles.subHeaderCellGreen]}>
                            <Text style={styles.subHeaderText}>Lean</Text>
                        </View>
                        <View style={[styles.tableCell, styles.metricCell, styles.subHeaderCell, styles.subHeaderCellGreen]}>
                            <Text style={styles.subHeaderText}>Walk</Text>
                        </View>
                        <View style={[styles.tableCell, styles.metricCell, styles.subHeaderCell, styles.subHeaderCellGreen]}>
                            <Text style={styles.subHeaderText}>Comp</Text>
                        </View>
                        <View style={[styles.tableCell, styles.metricCell, styles.subHeaderCell, styles.subHeaderCellGreen]}>
                            <Text style={styles.subHeaderText}>Safety</Text>
                        </View>
                        <View style={[styles.tableCell, styles.riskCell, styles.subHeaderCell, styles.subHeaderCellGreen]}>
                            <Text style={styles.subHeaderText}>Risk</Text>
                        </View>
                    </View>

                    {/* Data Rows */}
                    {evaluateResult && evaluateResult.length > 0 ? (
                        evaluateResult.map((result, index) => {
                            const isEven = index % 2 === 0;

                            return (
                                <View key={index} style={[styles.tableRow, isEven ? styles.evenRow : styles.oddRow]}>
                                    <View style={[styles.tableCell, styles.transportModeCell]}>
                                        <Text style={styles.cellText}>{result.TransitMode || "Not specified"}</Text>
                                    </View>
                                    <View style={[styles.tableCell, styles.distanceCell]}>
                                        <Text style={styles.cellText}>
                                            {result.Distance
                                                ? `${result.Distance} ${result.Distance_Unit || "miles"}`
                                                : "Not specified"}
                                        </Text>
                                    </View>

                                    {/* TreeModel (Random Forest) values */}
                                    <View style={[styles.tableCell, styles.metricCell]}>
                                        <Text style={styles.cellText}>{formatNumber(result?.TreeModel?.Lean)}</Text>
                                    </View>
                                    <View style={[styles.tableCell, styles.metricCell]}>
                                        <Text style={styles.cellText}>{formatNumber(result?.TreeModel?.Walk)}</Text>
                                    </View>
                                    <View style={[styles.tableCell, styles.metricCell]}>
                                        <Text style={styles.cellText}>{formatNumber(result?.TreeModel?.Compression)}</Text>
                                        {/* <Text style={styles.cellText}>-</Text> */}
                                    </View>
                                    <View style={[styles.tableCell, styles.metricCell]}>
                                        <Text style={styles.cellText}>{formatNumber(result?.TreeModel?.SafetyFactor)}</Text>
                                    </View>
                                    <View style={[styles.tableCell, styles.riskCell]}>
                                        {result?.TreeModel?.OverallRisk ? (
                                            <View style={getRiskBadgeStyle(result.TreeModel.OverallRisk)}>
                                                <Text style={styles.cellText}>{result.TreeModel.OverallRisk}</Text>
                                            </View>
                                        ) : (
                                            <Text style={styles.cellText}>-</Text>
                                        )}
                                    </View>

                                    {/* LinearRegression values */}
                                    <View style={[styles.tableCell, styles.metricCell]}>
                                        <Text style={styles.cellText}>{formatNumber(result?.LinearRegression?.Lean)}</Text>
                                    </View>
                                    <View style={[styles.tableCell, styles.metricCell]}>
                                        <Text style={styles.cellText}>{formatNumber(result?.LinearRegression?.Walk)}</Text>
                                    </View>
                                    <View style={[styles.tableCell, styles.metricCell]}>
                                        {/* <Text style={styles.cellText}>{formatNumber(result?.LinearRegression?.Compression)}</Text> */}
                                        <Text style={styles.cellText}>-</Text>
                                    </View>
                                    <View style={[styles.tableCell, styles.metricCell]}>
                                        <Text style={styles.cellText}>{formatNumber(result?.LinearRegression?.SafetyFactor)}</Text>
                                    </View>
                                    <View style={[styles.tableCell, styles.riskCell]}>
                                        {result?.LinearRegression?.OverallRisk ? (
                                            <View style={getRiskBadgeStyle(result.LinearRegression.OverallRisk)}>
                                                <Text style={styles.cellText}>{result.LinearRegression.OverallRisk}</Text>
                                            </View>
                                        ) : (
                                            <Text style={styles.cellText}>-</Text>
                                        )}
                                    </View>
                                </View>
                            );
                        })
                    ) : (
                        <View style={[styles.tableRow, styles.noDataRow]}>
                            <View style={[styles.tableCell, { width: '100%', borderRightWidth: 0 }]}>
                                <Text style={styles.noDataText}>
                                    No transport modes defined. Please add transport modes for analysis.
                                </Text>
                            </View>
                        </View>
                    )}
                </View>

                {/* Note */}
                <View style={styles.noteContainer}>
                    <Text style={styles.noteText}>
                        Note*: The prediction results presented above are based on the available input data and the assumptions of
                        the underlying model. Please ensure that the Test Case Filled Top Load values are comparable to those of the
                        identified nearest neighbours for meaningful interpretation. These predictions are indicative and subject to
                        variation due to input quality, environmental conditions, or other unmodeled factors. Always consult domain
                        experts or refer to additional data sources before making critical decisions based on these results.
                    </Text>
                </View>
            </View>
        </View>
    );
}; 